import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Heart, Mail, Lock, User, Globe, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SignupPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    why_here: '',
    avatar_emoji: '🌸',
    subscribe_newsletter: true
  });

  const avatarEmojis = ['🌸', '🦋', '🌙', '✨', '💫', '🌊', '🌱', '☀️', '🌺', '💜', '🕊️', '🌈'];
  
  const whyHereOptions = [
    { value: 'healing', label: 'Healing from past pain', emoji: '🌱' },
    { value: 'strength', label: 'Building inner strength', emoji: '💪' },
    { value: 'empowerment', label: 'Reclaiming my power', emoji: '✨' },
    { value: 'sisterhood', label: 'Finding community', emoji: '💜' }
  ];

  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error('Email and password are required');
      return;
    }

    // Generate session ID
    const sessionId = `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Store user data
    localStorage.setItem('user_session_id', sessionId);
    localStorage.setItem('user_profile', JSON.stringify(formData));
    localStorage.setItem('is_authenticated', 'true');
    
    // Subscribe to newsletter if opted in
    if (formData.subscribe_newsletter) {
      try {
        await axios.post(`${API}/newsletter/subscribe`, {
          email: formData.email,
          source: 'signup',
          user_session_id: sessionId
        });
      } catch (error) {
        console.error('Newsletter subscription error:', error);
        // Don't block signup if newsletter fails
      }
    }
    
    toast.success('Welcome to your safe space 💜');
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2E2536] via-[#3E324D] to-[#2E2536] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <Heart className="w-8 h-8 text-[#E7C6C2]" fill="#E7C6C2" />
            <h1 className="text-4xl font-bold text-[#EDE8F3]">SheSafe Haven</h1>
          </div>
          <p className="text-[#C8B5E0] text-lg">
            Your gentle space for healing begins here
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-[#3E324D] rounded-2xl p-8 shadow-2xl">
          <form onSubmit={handleSignup} className="space-y-6">
            {/* Step 1: Email & Password */}
            {step === 1 && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-[#EDE8F3] mb-4">Let's keep this simple</h2>
                
                <div>
                  <label className="block text-[#C8B5E0] text-sm mb-2">Your email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#C8B5E0]" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="you@example.com"
                      className="w-full bg-[#2E2536] text-[#EDE8F3] pl-11 pr-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E7C6C2]"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[#C8B5E0] text-sm mb-2">Create a password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#C8B5E0]" />
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="••••••••"
                      className="w-full bg-[#2E2536] text-[#EDE8F3] pl-11 pr-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E7C6C2]"
                      required
                      minLength={6}
                    />
                  </div>
                  <p className="text-xs text-[#C8B5E0] mt-1">At least 6 characters</p>
                </div>

                <Button
                  type="button"
                  onClick={() => setStep(2)}
                  className="w-full bg-[#E7C6C2] hover:bg-[#F7E5E5] text-[#3E324D] py-3 rounded-full font-semibold"
                >
                  Continue
                </Button>
              </div>
            )}

            {/* Step 2: Profile Details */}
            {step === 2 && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-[#EDE8F3] mb-4">Tell us a little about you</h2>
                <p className="text-sm text-[#C8B5E0] mb-4">(All optional — share what feels right)</p>

                {/* Name */}
                <div>
                  <label className="block text-[#C8B5E0] text-sm mb-2">What would you like to be called?</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#C8B5E0]" />
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Your name or nickname"
                      className="w-full bg-[#2E2536] text-[#EDE8F3] pl-11 pr-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E7C6C2]"
                    />
                  </div>
                </div>

                {/* Avatar Emoji */}
                <div>
                  <label className="block text-[#C8B5E0] text-sm mb-2">Pick a symbol for your space</label>
                  <div className="grid grid-cols-6 gap-2">
                    {avatarEmojis.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => setFormData({ ...formData, avatar_emoji: emoji })}
                        className={`text-3xl p-3 rounded-lg transition-all ${
                          formData.avatar_emoji === emoji
                            ? 'bg-[#E7C6C2] scale-110'
                            : 'bg-[#2E2536] hover:bg-[#4A3B5A]'
                        }`}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Why I'm Here */}
                <div>
                  <label className="block text-[#C8B5E0] text-sm mb-2">Why are you here?</label>
                  <div className="space-y-2">
                    {whyHereOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, why_here: option.value })}
                        className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                          formData.why_here === option.value
                            ? 'border-[#E7C6C2] bg-[#E7C6C2]/20'
                            : 'border-[#C8B5E0]/20 hover:border-[#C8B5E0]/40'
                        }`}
                      >
                        <span className="text-xl mr-2">{option.emoji}</span>
                        <span className="text-[#EDE8F3]">{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Newsletter Checkbox */}
                <div className="bg-[#3E324D] rounded-xl p-4 border border-[#E7C6C2]/20">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.subscribe_newsletter}
                      onChange={(e) => setFormData({ ...formData, subscribe_newsletter: e.target.checked })}
                      className="mt-1 w-5 h-5 rounded border-[#C8B5E0] text-[#E7C6C2] focus:ring-[#E7C6C2]"
                    />
                    <div>
                      <p className="text-[#EDE8F3] font-medium">Send me daily whispers via email 💜</p>
                      <p className="text-[#C8B5E0] text-xs mt-1">Gentle reminders and healing messages delivered to your inbox</p>
                    </div>
                  </label>
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    onClick={() => setStep(1)}
                    variant="ghost"
                    className="flex-1 text-[#C8B5E0] hover:bg-[#C8B5E0]/10"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-[#E7C6C2] hover:bg-[#F7E5E5] text-[#3E324D] py-3 rounded-full font-semibold"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Enter Your Space
                  </Button>
                </div>
              </div>
            )}
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-[#C8B5E0] text-sm">
              Already have a space?{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-[#E7C6C2] hover:text-[#F7E5E5] font-medium"
              >
                Welcome back
              </button>
            </p>
          </div>
        </div>

        {/* Privacy Note */}
        <p className="text-center text-[#C8B5E0] text-xs mt-6 max-w-sm mx-auto">
          Your data is private and secure. We'll never share your information.
        </p>
      </div>
    </div>
  );
};

export default SignupPage;
